import React, { useState, useEffect } from 'react';
import { Brain, RefreshCw, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { generateAICitations, updateAICitation } from '../../services/aiCitationsService';
import AdminLayout from '../../components/admin/AdminLayoutOptimized';

interface Prospect {
  id: string;
  prenom: string;
  nom: string;
  site_url: string;
}

interface Citation {
  platform: string;
  citations_count: number;
  pages_count: number;
  last_checked: string;
}

interface ProspectWithCitations extends Prospect {
  citations: Citation[];
}

const AICitationsManager: React.FC = () => {
  const [prospects, setProspects] = useState<ProspectWithCitations[]>([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingProspect, setEditingProspect] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, { citations: number; pages: number }>>({});

  const platforms = ['google', 'chatgpt', 'perplexity', 'gemini', 'copilot', 'grok'];

  useEffect(() => {
    fetchProspects();
  }, []);

  const fetchProspects = async () => {
    try {
      setLoading(true);
      
      // Fetch prospects
      const { data: prospectsData, error: prospectsError } = await supabase
        .from('prospects')
        .select('id, prenom, nom, site_url')
        .order('created_at', { ascending: false })
        .limit(50);

      if (prospectsError) throw prospectsError;

      // Fetch citations for all prospects
      const { data: citationsData, error: citationsError } = await supabase
        .from('ai_citations_results')
        .select('prospect_id, platform, citations_count, pages_count, last_checked');

      if (citationsError) throw citationsError;

      // Combine data
      const prospectsWithCitations: ProspectWithCitations[] = (prospectsData || []).map(prospect => {
        const prospectCitations = (citationsData || [])
          .filter((c: any) => c.prospect_id === prospect.id)
          .map((c: any) => ({
            platform: c.platform,
            citations_count: c.citations_count,
            pages_count: c.pages_count,
            last_checked: c.last_checked,
          }));

        return {
          ...prospect,
          citations: prospectCitations,
        };
      });

      setProspects(prospectsWithCitations);
    } catch (error) {
      console.error('Error fetching prospects:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des données' });
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async (prospectId: string) => {
    try {
      setRegenerating(prospectId);
      setMessage(null);

      const success = await generateAICitations(prospectId);

      if (success) {
        setMessage({ type: 'success', text: 'Citations régénérées avec succès !' });
        await fetchProspects(); // Refresh data
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de la régénération' });
      }
    } catch (error) {
      console.error('Error regenerating citations:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la régénération' });
    } finally {
      setRegenerating(null);
    }
  };

  const handleEdit = (prospectId: string, citations: Citation[]) => {
    setEditingProspect(prospectId);
    const values: Record<string, { citations: number; pages: number }> = {};
    
    platforms.forEach(platform => {
      const citation = citations.find(c => c.platform === platform);
      values[platform] = {
        citations: citation?.citations_count || 0,
        pages: citation?.pages_count || 0,
      };
    });
    
    setEditValues(values);
  };

  const handleSave = async (prospectId: string) => {
    try {
      setSaving(prospectId);
      setMessage(null);

      // Save all platforms
      const promises = platforms.map(platform => 
        updateAICitation(
          prospectId,
          platform,
          editValues[platform].citations,
          editValues[platform].pages
        )
      );

      await Promise.all(promises);

      setMessage({ type: 'success', text: 'Citations mises à jour avec succès !' });
      setEditingProspect(null);
      await fetchProspects(); // Refresh data
    } catch (error) {
      console.error('Error saving citations:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(null);
    }
  };

  const getCitationValue = (prospect: ProspectWithCitations, platform: string): { citations: number; pages: number } => {
    if (editingProspect === prospect.id) {
      return editValues[platform] || { citations: 0, pages: 0 };
    }
    
    const citation = prospect.citations.find(c => c.platform === platform);
    return {
      citations: citation?.citations_count || 0,
      pages: citation?.pages_count || 0,
    };
  };

  const updateEditValue = (platform: string, field: 'citations' | 'pages', value: number) => {
    setEditValues(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: Math.max(0, value),
      },
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Brain className="w-8 h-8 text-orange-500" />
            Gestion des Citations IA
          </h1>
          <p className="text-gray-600 mt-2">
            Gérez les citations générées par les plateformes IA pour chaque prospect
          </p>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            {message.text}
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prospect
                  </th>
                  {platforms.map(platform => (
                    <th key={platform} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {platform}
                    </th>
                  ))}
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {prospects.map(prospect => {
                  const isEditing = editingProspect === prospect.id;
                  const isRegenerating = regenerating === prospect.id;
                  const isSaving = saving === prospect.id;

                  return (
                    <tr key={prospect.id} className={isEditing ? 'bg-orange-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {prospect.prenom} {prospect.nom}
                          </div>
                          <div className="text-sm text-gray-500">{prospect.site_url}</div>
                        </div>
                      </td>
                      
                      {platforms.map(platform => {
                        const value = getCitationValue(prospect, platform);
                        return (
                          <td key={platform} className="px-4 py-4 text-center">
                            {isEditing ? (
                              <div className="space-y-1">
                                <input
                                  type="number"
                                  min="0"
                                  value={value.citations}
                                  onChange={(e) => updateEditValue(platform, 'citations', parseInt(e.target.value) || 0)}
                                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                                  placeholder="Cit."
                                />
                                <input
                                  type="number"
                                  min="0"
                                  value={value.pages}
                                  onChange={(e) => updateEditValue(platform, 'pages', parseInt(e.target.value) || 0)}
                                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                                  placeholder="Pgs"
                                />
                              </div>
                            ) : (
                              <div className="text-sm">
                                <div className="font-semibold text-gray-900">{value.citations}</div>
                                <div className="text-gray-500 text-xs">{value.pages} pages</div>
                              </div>
                            )}
                          </td>
                        );
                      })}

                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleSave(prospect.id)}
                              disabled={isSaving}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                            >
                              {isSaving ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Save className="w-4 h-4 mr-1" />
                                  Sauver
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => setEditingProspect(null)}
                              disabled={isSaving}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                              Annuler
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(prospect.id, prospect.citations)}
                              className="text-orange-600 hover:text-orange-900"
                            >
                              Éditer
                            </button>
                            <button
                              onClick={() => handleRegenerate(prospect.id)}
                              disabled={isRegenerating}
                              className="inline-flex items-center text-blue-600 hover:text-blue-900 disabled:opacity-50"
                            >
                              {isRegenerating ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                  Régénérer
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {prospects.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Aucun prospect trouvé
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AICitationsManager;
